import { BillingModalContext } from './Context'

import { useContext, useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useCallbackPrompt } from 'Hooks/useCallbackPrompt'
import { useBillingAddressStore, INITIAL_STATE } from 'Store/BillingAddress'
import { useGeoStore } from 'Store/Geo'
import { shallow } from 'zustand/shallow'
import { toast } from 'react-toastify'
import * as Yup from 'yup'

const ViewModel = () => {
	const validationSchema = Yup.object().shape({
		title: Yup.string()
			.min(3, 'กรุณากรอกชื่อเรียกที่อยู่อย่างน้อย 3 ตัวอักษร')
			.required('กรุณากรอกชื่อเรียกที่อยู่'),
		taxId: Yup.string().test(
			'taxIdValidation',
			'กรุณากรอกเลขประจำตัวผู้เสียภาษีให้ถูกต้อง',
			value => {
				if (value) {
					if (value.replaceAll(/-/g, '').length === 13) {
						return true
					} else {
						return false
					}
				} else {
					return false
				}
			}
		),
		receiverPhone: Yup.string().test({
			name: 'receiverPhoneValidation',
			message: 'กรุณากรอกหมายเลขโทรศัพท์ให้ถูกต้อง',
			test: value => {
				if (value) {
					if (value.replaceAll(/-/g, '').length === 10) {
						return true
					} else {
						return false
					}
				} else {
					return false
				}
			},
		}),
		address: Yup.string()
			.min(1, 'กรุณากรอกที่อยู่โดยละเอียด')
			.required('กรุณากรอกที่อยู่โดยละเอียด'),
		provinceId: Yup.number().not([0], 'กรุณาเลือกจังหวัด').required('กรุณาเลือกจังหวัด'),
		amphureId: Yup.number().not([0], 'กรุณาเลือกอำเภอ').required('กรุณาเลือกอำเภอ'),
		tumbonId: Yup.number().not([0], 'กรุณาเลือกตำบล').required('กรุณาเลือกตำบล'),
	})
	const { id } = useParams()
	const { open, formType, setOpen, setFormType } = useContext(BillingModalContext)
	const {
		selectedBillingAddress,
		setSelectedBillingAddress,
		getAllBillingAddress,
		createBillingAddress,
		updateBillingAddress,
	} = useBillingAddressStore(
		state => ({
			selectedBillingAddress: state.selected,
			setSelectedBillingAddress: state.setSelected,
			getAllBillingAddress: state.getAll,
			createBillingAddress: state.create,
			updateBillingAddress: state.edit,
		}),
		shallow
	)
	const {
		provinces,
		amphures,
		tumbons,
		setAmphures,
		setTumbons,
		getProvinces,
		getAmphures,
		getTumbons,
		clearGeoState,
	} = useGeoStore(
		state => ({
			provinces: state.provinces,
			amphures: state.amphures,
			setAmphures: state.setAmphures,
			tumbons: state.tumbons,
			setTumbons: state.setTumbons,
			getProvinces: state.getProvinces,
			getAmphures: state.getAmphures,
			getTumbons: state.getTumbons,
			clearGeoState: state.clearState,
		}),
		shallow
	)
	const [billingFormState, setBillingFormState] = useState(INITIAL_STATE)
	const [hasChanged, setHasChanged] = useState<boolean>(false)
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
	const zipCode = tumbons.find(t => t.id === billingFormState.tumbonId)?.zipCode
	const [isLoadingProvinces, setIsLoadingProvinces] = useState<boolean>(false)
	const [isLoadingAmphures, setIsLoadingAmphures] = useState<boolean>(false)
	const [isLoadingTumbons, setIsLoadingTumbons] = useState<boolean>(false)
	const [errors, setErrors] = useState<{ [key: string]: any }>({})

	useCallbackPrompt(hasChanged)

	const handleSetIntialState = () => {
		if (open) {
			if (formType === 'add') {
				setBillingFormState(INITIAL_STATE)
			} else if (formType === 'edit') {
				setBillingFormState(selectedBillingAddress)

				setIsLoadingProvinces(true)
				setIsLoadingAmphures(true)
				setIsLoadingTumbons(true)

				getProvinces().then(() => {
					setIsLoadingProvinces(false)
				})
				getAmphures(selectedBillingAddress.provinceId).then(() => {
					setIsLoadingAmphures(false)
				})
				getTumbons(selectedBillingAddress.amphureId).then(() => {
					setIsLoadingTumbons(false)
				})
			}
		}
	}

	const fetchOptions = () => {
		if (open) {
			setIsLoadingProvinces(true)
			getProvinces().then(() => {
				setIsLoadingProvinces(false)
			})
		}
	}

	const onChangeBillingFormState = (name: string, value: any) => {
		setBillingFormState(prevState => ({
			...prevState,
			[name]: value,
		}))
		setHasChanged(true)

		if (name === 'provinceId') {
			setIsLoadingAmphures(true)
			if (value) {
				getAmphures(value).then(() => {
					setIsLoadingAmphures(false)
				})
			} else {
				setAmphures([])
			}
		} else if (name === 'amphureId') {
			setIsLoadingTumbons(true)
			if (value) {
				getTumbons(value).then(() => {
					setIsLoadingTumbons(false)
				})
			} else {
				setTumbons([])
			}
		}
	}

	const handleClose = () => {
		setFormType('add')
		setSelectedBillingAddress(INITIAL_STATE)
		setOpen(false)
		setHasChanged(false)
		clearGeoState()
		setErrors({})
	}

	const handleSubmit = async () => {
		setIsSubmitting(true)
		validationSchema
			.validate(billingFormState, { abortEarly: false })
			.then(async () => {
				setErrors({})

				if (formType === 'add') {
					if (
						!hasChanged ||
						Object.keys(billingFormState).some(key => {
							if (['id', 'buyerId', 'isActive', 'address'].includes(key)) return false

							return (
								// @ts-ignore
								!billingFormState[key] ||
								// @ts-ignore
								billingFormState[key] === '' ||
								// @ts-ignore
								billingFormState[key] === null
							)
						})
					) {
						toast.error('กรุณากรอกข้อมูลให้ครบถ้วน')
						setIsSubmitting(false)
						return
					} else {
						let body: { [key: string]: any } = {}

						Object.entries(billingFormState).forEach(([key, value]) => {
							if (['id', 'buyerId', 'isActive'].includes(key)) {
								return
							} else if (key === 'taxId' || key === 'receiverPhone') {
								body[key] = String(value).replaceAll('-', '')
							} else if (key === 'address') {
								body[key] = String(value).trim()
							} else {
								if (value || String(value).length !== 0) {
									body[key] = value
								}
							}
						})

						body['isActive'] = false

						const { success, data } = await createBillingAddress(body)
						if (!success) {
							toast.error(data)
							setIsSubmitting(false)
						} else {
							toast.success('สร้างที่อยู่ออกใบกำกับภาษีสำเร็จ')
							setSelectedBillingAddress(INITIAL_STATE)
							setBillingFormState(INITIAL_STATE)
							setHasChanged(false)
							getAllBillingAddress(id ?? '')
							setOpen(false)
							setIsSubmitting(false)
						}
					}
				} else if (formType === 'edit') {
					if (!hasChanged) {
						return
					} else {
						let body: { [key: string]: any } = {}

						Object.keys(selectedBillingAddress).forEach(key => {
							if (key === 'id' || key === 'isActive' || key === 'buyerId') {
								return
							} else if (key === 'taxId' || key === 'receiverPhone') {
								if (billingFormState[key].replaceAll('-', '') !== selectedBillingAddress[key]) {
									body[key] = billingFormState[key].replaceAll('-', '')
								}
							} else {
								// @ts-ignore
								if (billingFormState[key] !== selectedBillingAddress[key]) {
									// @ts-ignore
									body[key] = billingFormState[key]
								}
							}
						})

						const { success, data } = await updateBillingAddress(selectedBillingAddress.id, body)
						if (!success) {
							toast.error(data)
							setIsSubmitting(false)
						} else {
							toast.success('แก้ไขที่อยู่ออกใบกำกับภาษีสำเร็จ')
							setSelectedBillingAddress(INITIAL_STATE)
							setBillingFormState(INITIAL_STATE)
							setHasChanged(false)
							getAllBillingAddress(id ?? '')
							setOpen(false)
							setIsSubmitting(false)
						}
					}
				}
			})
			.catch(err => {
				let errors: { [key: string]: any } = {}
				err.inner.forEach((e: any) => {
					errors[e.path] = e.message
				})

				setErrors(errors)
				setIsSubmitting(false)
			})
	}

	useEffect(() => {
		handleSetIntialState()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, formType, selectedBillingAddress])

	useEffect(() => {
		fetchOptions()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open])

	return {
		open,
		formType,
		billingFormState,
		onChangeBillingFormState,
		handleClose,
		handleSubmit,
		isLoadingProvinces,
		isLoadingAmphures,
		isLoadingTumbons,
		provinces: provinces.map(p => ({
			label: `${p.nameTh} - ${p.nameEn}`,
			value: p.id,
		})),
		amphures: amphures.map(a => ({
			label: `${a.nameTh} - ${a.nameEn}`,
			value: a.id,
		})),
		tumbons: tumbons.map(t => ({
			label: `${t.nameTh} - ${t.nameEn}`,
			value: t.id,
		})),
		zipCode,
		errors,
		isSubmitting,
	}
}

export default ViewModel
